import { Component, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { MatSidenav, MatDrawerContainer, MatDrawer } from '@angular/material/sidenav';
import { CollaborationService } from '../../services/collaboration.service';
import { has, isEqual } from 'lodash';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NotificationSettings } from '../../interfaces/notification-settings';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from '../snackbar/snackbar.component';
import { Subject, takeUntil } from 'rxjs';
import { SpinnerComponent } from '../spinner/spinner.component';
import { NgClass } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { AssetUrlPipe } from '../../pipes/asset-url.pipe';

@Component({
    selector: 'app-notification-settings',
    templateUrl: './notification-settings.component.html',
    styleUrl: './notification-settings.component.scss',
    imports: [SpinnerComponent, MatDrawerContainer, MatDrawer, NgClass, MatTooltip, FormsModule, ReactiveFormsModule, MatSlideToggle, AssetUrlPipe]
})
export class NotificationSettingsComponent implements OnInit, OnDestroy {
  @Input() name = "notificationDrawer";
  @Input() isMobile = false;
  isDrawerOpened = false;
  @ViewChild('drawer', { static: true }) private drawer!: MatSidenav;
  notificationSettingsFg!: FormGroup;
  destroy$: Subject<boolean> = new Subject<boolean>();
  notificationSettingsObj = {
    mobile: {
      displayOptions: [
        {
          label: 'Notifications',
          name: 'mobileNotifications'
        }        
      ],
      formGroup: {
        mobileNotifications: [false]
      }
    },
    desktop: {
      displayOptions: [
        {
          label: 'At Mentions',
          name: 'atMentions'
        },
        {
          label: 'Like Post',
          name: 'likePosts'
        },
        {
          label: 'Like Comment',
          name: 'likeComments'
        },
        {
          label: 'Follow Posts',
          name: 'followPosts'
        },
        {
          label: 'Follow Comments',
          name: 'followComments'
        },
        {
          label: 'Follow spaces',
          name: 'spaces'
        },
      ],
      formGroup: {
        atMentions: [false],
        likePosts: [false],
        likeComments: [false],
        followPosts: [false],
        followComments: [false],
        spaces: [false]
      }
    }
  }
  notificationSettingsFields;
  initialValue!: NotificationSettings;
  emailId: string = '';
  showLoader = false;
  isApplicationRunningOnOnItSystem: boolean = false;
  constructor(private collaborationService: CollaborationService, private fb: FormBuilder, private snackBar: MatSnackBar, private renderer: Renderer2) {

  }

  ngOnInit(): void {
    if (this.isMobile) {
      this.notificationSettingsFg = this.fb.group(this.notificationSettingsObj.mobile.formGroup);
      this.notificationSettingsFields = this.notificationSettingsObj.mobile.displayOptions;
    } else {
      this.notificationSettingsFg = this.fb.group(this.notificationSettingsObj.desktop.formGroup);
      this.notificationSettingsFields = this.notificationSettingsObj.desktop.displayOptions;
    }
    this.isApplicationRunningOnOnItSystem = this.collaborationService.isApplicationRunningOnOnItSystem;
    this.collaborationService.showOrHideSideNav.pipe(takeUntil(this.destroy$)).subscribe((x: { [key: string]: boolean }) => {
      if (has(x, this.name)) {
        this.isDrawerOpened = x[this.name];
        const targetElement = document.querySelector('.forms-navigation__container-view-content');
        if (targetElement && this.isDrawerOpened) {
          this.renderer.addClass(targetElement, 'increase-z-index');
        } else if (targetElement) {
          this.renderer.removeClass(targetElement, 'increase-z-index');
        }
      }
      if (this.isDrawerOpened) {
        this.getNotificationSettings();
      }
    });
  }

  backdropClick() {
    // this.isDrawerOpened = false;
    this.drawer.close();
    this.collaborationService.setShowOrHideSideNav(this.name, false);
  }

  updateNotificationSettings() {
    this.backdropClick();
    const updatedNotificationSettings = {
      ...this.initialValue,
      ...this.notificationSettingsFg.value
    }
    if (isEqual(this.initialValue, updatedNotificationSettings)) {
      return;
    }
    this.showLoader = true;
    this.emailId = this.collaborationService.userEmail;
    this.collaborationService.updateNotificationSettings(updatedNotificationSettings, this.emailId).subscribe({
      next: (res: any) => {
        this.showLoader = false;
        if (res.data) {
          this.collaborationService.notificationSettings = updatedNotificationSettings;
          this.initialValue = updatedNotificationSettings;
          this.snackBar.openFromComponent(SnackbarComponent, {
            panelClass: 'custom-snackbar',
            duration: 1000,
            data: {
              snackBar: this.snackBar,
              message: `Notification settings updated successfully.`,
            },
          });
          const enabledLabels = this.notificationSettingsFields
            .filter((field: any) => updatedNotificationSettings[field.name])
            .map((field: any) => field.label)
            .join('|');
          (window as any).dataLayer = (window as any).dataLayer || [];
          (window as any).dataLayer.push({
            event: 'referral_notification_settings',
            eventLabel: enabledLabels,
            eventPage: 'Referral Actions',
          });
        }
      },
      error: (err: any) => {
        this.showLoader = false;
        this.collaborationService.displayErrorMessage(err, 'Error while updating the data for Notification details request. Please try after some time.');
      }
    });
  }

  getNotificationSettings() {
    if (this.collaborationService.notificationSettings) {
      this.initialValue = this.collaborationService.notificationSettings;
      this.notificationSettingsFg.patchValue(this.collaborationService.notificationSettings);
      return;
    }
    this.showLoader = true;
    this.emailId = this.collaborationService.userEmail;
    this.collaborationService.getNotificationSettings(this.emailId).subscribe({
      next: (res: any) => {
        this.showLoader = false;
        if (res.data) {
          this.initialValue = res.data;
          this.collaborationService.notificationSettings = res.data;
          this.notificationSettingsFg.patchValue(res.data)
        }
      },
      error: (err: any) => {
        this.showLoader = false;
        this.collaborationService.displayErrorMessage(err, 'Error while retrieving the data for Notification details request. Please try after some time.');
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
